import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, X, ChevronDown, Phone } from 'lucide-react';

const services = [
  { name: 'IRS Notice Review', path: '/services/irs-notice' },
  { name: 'Tax Diagnostic Review', path: '/services/diagnostic' },
  { name: 'Tax Resolution Services', path: '/services/resolution' },
  { name: 'Tax Preparation', path: '/services/preparation' },
  { name: 'Small Business Tax Help', path: '/services/small-business' },
  { name: 'Truck Driver Tax Help', path: '/services/truck-driver-tax' },
  { name: 'Second Look Review — Free', path: '/services/second-look-review' },
];

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Free Consultation', path: '/free-consultation' },
  { name: 'Ask a Question - Free', path: '/ask' },
  { name: 'Pricing', path: '/tax-relief-pricing' },
  { name: 'About', path: '/about' },
  { name: 'Blog', path: '/blog' },
];

export default function Navbar() {
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="main-navbar"
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/60"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-2">
        {/* Logo */}
        <Link to="/" data-testid="navbar-logo" className="flex items-center overflow-hidden h-16 w-40 sm:h-[120px] sm:w-[320px]">
          <img
            src="https://customer-assets.emergentagent.com/job_irs-notice-review/artifacts/w4s948ey_Log%20for%20TaxPro%20-%20Answers%20-ChatGPT%20Image%20Apr%2017%2C%202026%2C%2009_28_08%20AM.png"
            alt="TaxPro - Answers"
            className="w-full h-auto rounded"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.path.replace(/\//g, '') || 'home'}`}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive(link.path)
                  ? 'text-brand-navy bg-slate-100'
                  : 'text-slate-600 hover:text-brand-navy hover:bg-slate-50'
              }`}
            >
              {link.name}
            </Link>
          ))}

          {/* Services Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              data-testid="nav-services-dropdown"
              className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                location.pathname.startsWith('/services')
                  ? 'text-brand-navy bg-slate-100'
                  : 'text-slate-600 hover:text-brand-navy hover:bg-slate-50'
              }`}
            >
              Services
              <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
            </button>
            {servicesOpen && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                {services.map((service) => (
                  <Link
                    key={service.path}
                    to={service.path}
                    data-testid={`nav-service-${service.path.split('/').pop()}`}
                    className={`block px-4 py-2.5 text-sm transition-colors ${
                      isActive(service.path)
                        ? 'text-brand-navy bg-slate-50 font-medium'
                        : 'text-slate-600 hover:text-brand-navy hover:bg-slate-50'
                    }`}
                  >
                    {service.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <a href="tel:8555892461" className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-navy transition-colors">
            <Phone className="w-4 h-4" />
            <span className="font-medium">855-589-2461</span>
          </a>
          <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer">
            <Button
              data-testid="nav-book-now-btn"
              className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold rounded-md px-5"
            >
              Book a Consultation
            </Button>
          </a>
        </div>

        {/* Mobile Menu */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <button data-testid="mobile-menu-trigger" className="p-2 text-brand-navy">
              <Menu className="w-6 h-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-80 p-0">
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
              <span className="font-heading font-bold text-lg text-brand-navy">TaxPro - Answers</span>
              </div>
              <nav className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileOpen(false)}
                    className={`px-4 py-3 text-sm font-medium rounded-md ${
                      isActive(link.path) ? 'text-brand-navy bg-slate-100' : 'text-slate-600'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="px-4 py-3">
                  <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Services</span>
                </div>
                {services.map((service) => (
                  <Link
                    key={service.path}
                    to={service.path}
                    onClick={() => setMobileOpen(false)}
                    className={`px-6 py-2.5 text-sm rounded-md ${
                      isActive(service.path) ? 'text-brand-navy bg-slate-100 font-medium' : 'text-slate-600'
                    }`}
                  >
                    {service.name}
                  </Link>
                ))}
              </nav>
              <div className="mt-8 pt-6 border-t border-slate-200 space-y-4">
                <a href="tel:8555892461" className="flex items-center gap-2 text-sm text-slate-600">
                  <Phone className="w-4 h-4" /> 855-589-2461
                </a>
                <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)}>
                  <Button className="w-full bg-brand-green hover:bg-brand-green-hover text-white font-semibold">
                    Book a Consultation
                  </Button>
                </a>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
